import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

// Premium Category Form - Organization Excellence
function CategoryForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        name: '',
        parent_id: '',
        description: ''
    });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadCategories();
        if (isEdit) {
            loadCategory();
        }
    }, [id]);

    const loadCategories = async () => {
        try {
            const response = await api.get('/categories');
            if (response.success) {
                // Filter out current category from parents if editing
                setCategories(response.data.filter((c: any) => String(c.id) !== String(id)));
            }
        } catch (err) {
            console.error('Failed to load categories', err);
        }
    };

    const loadCategory = async () => {
        try {
            const response = await api.get(`/categories/${id}`);
            if (response.success) {
                setFormData({
                    name: response.data.name || '',
                    parent_id: response.data.parent_id || '',
                    description: response.data.description || ''
                });
            } else {
                const msg = response.message || 'Failed to load category';
                setError(msg);
                toast.error(msg);
            }
        } catch (err: any) {
            const msg = err.message || 'Error fetching category';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);

            const response = isEdit
                ? await api.put(`/categories/${id}`, { category: formData })
                : await api.post('/categories', { category: formData });

            if (response.success) {
                toast.success(`Category ${isEdit ? 'updated' : 'created'} successfully`);
                navigate('/categories');
            } else {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || 'Failed to save category'];
                
                setError(backendErrors.join(", "));
                toast.error(backendErrors[0]);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err: any) {
            const msg = err.message || 'An unexpected error occurred';
            setError(msg);
            toast.error(msg);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-medium tracking-widest uppercase text-[10px]">Synchronizing Hierarchy...</div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header section */}
            <div className="flex items-center gap-6 mb-12">
                <Link to="/categories" className="w-14 h-14 bg-white border border-slate-100 rounded-2xl shadow-xl shadow-slate-200/50 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:scale-110 transition-all duration-300">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                </Link>
                <div>
                    <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight leading-none">
                        Category <span className="text-indigo-600">{isEdit ? 'Architect' : 'Designer'}</span>
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium italic">Define the structural hierarchy for product classification</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                    <div className="bg-rose-50 border-l-4 border-rose-500 p-6 rounded-3xl mb-10 shadow-sm animate-in shake duration-500 flex items-center gap-4">
                        <span className="text-2xl">⚠️</span>
                        <p className="text-sm font-black text-rose-800 uppercase tracking-wider">{error}</p>
                    </div>
                )}

                {/* Identity Card */}
                <div className="bg-white rounded-lg border border-slate-100 shadow-2xl shadow-slate-200/50 p-10 relative overflow-hidden group">
                    <div className="relative z-10">
                        <h3 className="text-lg font-black text-slate-900 tracking-tight mb-8 flex items-center gap-3">
                            <span className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-sm shadow-inner">🔖</span>
                            Category Identity
                        </h3>

                        <div className="space-y-8">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 ml-1">Classification Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="E.G., ELECTRONICS, LUXURY GOODS"
                                    className="w-full h-16 px-8 bg-slate-50 border-none rounded-2xl text-sm font-black text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all uppercase tracking-widest placeholder:opacity-30"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 ml-1">Parent Nexus (Optional)</label>
                                <div className="relative">
                                    <select
                                        name="parent_id"
                                        value={formData.parent_id}
                                        onChange={handleChange}
                                        className="w-full h-16 px-8 bg-slate-50 border-none rounded-2xl text-sm font-black text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer uppercase tracking-widest"
                                    >
                                        <option value="">ROOT LEVEL (BASE)</option>
                                        {categories.map((cat: any) => (
                                            <option key={cat.id} value={cat.id}>{cat.name?.toUpperCase()}</option>
                                        ))}
                                    </select>
                                    <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                                    </div>
                                </div>
                                <p className="mt-3 text-[10px] font-bold text-slate-400 italic ml-1 leading-relaxed">
                                    Nest this classification within a broader group to establish a taxonomy.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50/30 blur-3xl -mr-32 -mt-32 rounded-full group-hover:scale-125 transition-transform duration-1000"></div>
                </div>

                {/* Description Card */}
                <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-10">
                    <h3 className="text-lg font-black text-slate-900 tracking-tight mb-8 flex items-center gap-3">
                        <span className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center text-sm shadow-inner">📝</span>
                        Contextual Metadata
                    </h3>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={5}
                        placeholder="PROVIDE STRATEGIC CONTEXT FOR THIS CATEGORY..."
                        className="w-full px-8 py-6 bg-slate-50 border-none rounded-lg text-sm font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all hover:bg-slate-100 resize-none uppercase tracking-widest placeholder:opacity-30"
                    />
                </div>

                {/* Final Actions */}
                <div className="pt-10 flex flex-col sm:flex-row items-center gap-6">
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full sm:w-auto px-12 py-6 bg-indigo-600 text-white rounded-lg text-xs font-black uppercase tracking-[0.3em] shadow-2xl shadow-indigo-200 hover:bg-slate-900 hover:shadow-slate-300 transition-all duration-500 disabled:opacity-50"
                    >
                        {saving ? 'Synchronizing Archive...' : (isEdit ? 'Commit Changes' : 'Initialize Category')}
                    </button>
                    <Link
                        to="/categories"
                        className="w-full sm:w-auto px-12 py-6 bg-white border border-slate-100 text-slate-400 rounded-lg text-xs font-black uppercase tracking-[0.3em] hover:bg-slate-50 transition-all duration-500 text-center"
                    >
                        Abort Registry
                    </Link>

                    <div className="sm:ml-auto flex items-center gap-4 text-slate-400">
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                        <span className="text-[10px] font-black uppercase tracking-widest italic opacity-70">Architecture: Active</span>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default CategoryForm;
